import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

/**
 * Spotify OAuth redirect target. Spotify sends the user back here with
 * `?code=...&state=...`. We exchange the code via our edge function, then
 * bounce the user back to where they started.
 */
export default function SpotifyCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"working" | "ok" | "err">("working");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = params.get("code");
    const state = params.get("state");
    const err = params.get("error");
    const statePayload = (() => {
      if (!state) return null;
      try {
        const normalized = state.replace(/-/g, "+").replace(/_/g, "/");
        const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
        return JSON.parse(atob(padded)) as { redirect_uri?: string; return_to?: string };
      } catch {
        return null;
      }
    })();
    const expectedState = sessionStorage.getItem("spotify_oauth_state");
    // Fall back to reconstructing the redirect_uri from the current URL when
    // sessionStorage is empty (e.g. storage partitioning between the tab that
    // started OAuth and the tab Spotify redirected back to).
    const redirectUri =
      sessionStorage.getItem("spotify_oauth_redirect") ||
      statePayload?.redirect_uri ||
      `${window.location.origin}/spotify/callback`;
    const returnTo = sessionStorage.getItem("spotify_return_to") || statePayload?.return_to || "/";

    (async () => {
      if (err) {
        setErrorMsg(err === "access_denied" ? "You cancelled Spotify sign-in." : err);
        setStatus("err");
        return;
      }
      if (!code) {
        setErrorMsg(
          "Spotify didn't return an authorization code. Please go back and tap “Soundtrack this trip” again."
        );
        setStatus("err");
        return;
      }
      // Only treat state mismatch as fatal when we actually have a stored
      // value to compare against. If storage was cleared, trust the round-trip.
      if (expectedState && state && state !== expectedState) {
        setErrorMsg("Security check failed (state mismatch). Please try again.");
        setStatus("err");
        return;
      }

      const { data, error } = await supabase.functions.invoke("spotify-auth-callback", {
        body: { code, redirect_uri: redirectUri },
      });
      if (error || data?.error) {
        setErrorMsg(error?.message || data?.error || "Failed to connect Spotify");
        setStatus("err");
        return;
      }

      sessionStorage.removeItem("spotify_oauth_state");
      sessionStorage.removeItem("spotify_oauth_redirect");
      sessionStorage.removeItem("spotify_return_to");
      setStatus("ok");
      // Bounce back so the user can hit "Soundtrack this trip" again.
      setTimeout(() => navigate(returnTo, { replace: true }), 900);
    })();
  }, [params, navigate]);

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-sm w-full text-center space-y-4">
        {status === "working" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Connecting your Spotify…</p>
          </>
        )}
        {status === "ok" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#1DB954]" />
            <p className="heading text-xl">Spotify connected</p>
            <p className="text-sm text-muted-foreground">Taking you back…</p>
          </>
        )}
        {status === "err" && (
          <>
            <AlertTriangle className="w-10 h-10 mx-auto text-destructive" />
            <p className="heading text-xl">Couldn't connect Spotify</p>
            <p className="text-sm text-muted-foreground break-words">{errorMsg}</p>
            <Button onClick={() => navigate("/app", { replace: true })} className="rounded-full">
              Back to map
            </Button>
          </>
        )}
      </div>
    </main>
  );
}
